<template>
  <div>
    <div v-if="isLoading || !isMinimumLoadingTime" class="relative">
      <img
        src="https://images.ctfassets.net/wmwzystk5o45/10wQtroaMAsqug9haLksrh/117b45d0e8e38fa2be90078eaa5c0696/Screenshot_2026-01-23_at_5.09.02%C3%A2__PM.png?fm=avif&w=500&q=100"
        class="w-full"
        alt=""
      />
      <Card class="card-preserve !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-24 !rounded-4xl">
        <div class="flex gap-8 items-center whitespace-nowrap">
          <LoadingSpinner />
          <p class="text-h3 font-regular">Finding sitters</p>
        </div>
      </Card>
    </div>
    <div v-else class="mx-auto w-full max-w-536">
      <Card class="form-stepper-card sm:mt-16 box-shadow-none sm:box-shadow-sm sm:p-24">
        <h1 :class="headlineClass">{{ resultsHeadline }}</h1>
        <p :class="descriptionClass">{{ config.description }}</p>
        <Button
          class="w-full"
          size="large"
          :variant="config.primaryButton.variant"
          :value="config.primaryButton.label"
          @click="$emit('action', config.primaryButton.action)"
        />
        <template v-if="config.secondarySection">
          <hr class="border-grey-divider-subdued my-24" />
          <p :class="secondaryDescriptionClass">{{ config.secondarySection.description }}</p>
          <Button
            class="w-full"
            size="large"
            :variant="config.secondarySection.button.variant"
            :value="config.secondarySection.button.label"
            @click="$emit('action', config.secondarySection.button.action)"
          />
        </template>
      </Card>
      <div
        class="mt-16 flex w-full items-start gap-8 border-1 border-solid border-grey-divider-subdued rounded-xl bg-grey-background-contrast px-16 py-8"
      >
        <span class="text-small sm:text-body leading-none flex-shrink-0">💡</span>
        <p class="text-small sm:text-body mb-0">{{ employerBenefitCalloutText }}</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import Card from '@/components/card/Card.vue';
import Button from '@/components/button/Button.vue';
import LoadingSpinner from '@/components/spinner/Spinner.vue';
import {
  EMPLOYER_BENEFIT_CALLOUT_TEXT,
  LOW_SITTER_COUNT_HEADLINE,
  MIN_SITTER_COUNT_FOR_HEADLINE,
  SITTER_COUNT_FETCH_ERROR_HEADLINE,
  type CorporateOnboardingCareNearYouAction,
} from './types/careNearYouConstants';
import type { CareNearYouConfig } from './careNearYouConfig';

export default {
  name: 'CorporateOnboardingGuidedCareNearYouContent',
  components: { Card, Button, LoadingSpinner },
  props: {
    config: {
      type: Object as PropType<CareNearYouConfig>,
      required: true,
    },
    sitterCount: {
      type: Number as PropType<number | null>,
      default: null,
    },
    hasFetchError: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    isMinimumLoadingTime: {
      type: Boolean,
      required: true,
    },
  },
  emits: {
    action: (_action: CorporateOnboardingCareNearYouAction) => true,
  },
  computed: {
    employerBenefitCalloutText(): string {
      return EMPLOYER_BENEFIT_CALLOUT_TEXT;
    },
    resultsHeadline(): string {
      if (this.hasFetchError) {
        return SITTER_COUNT_FETCH_ERROR_HEADLINE;
      }

      return this.sitterCountHeadline;
    },
    sitterCountHeadline(): string {
      if (this.sitterCount === null) {
        return LOW_SITTER_COUNT_HEADLINE;
      }

      if (this.sitterCount >= MIN_SITTER_COUNT_FOR_HEADLINE) {
        return `${this.sitterCount} sitters near you!`;
      }

      return LOW_SITTER_COUNT_HEADLINE;
    },
    headlineClass(): string {
      return 'centered mb-16 text-h1';
    },
    descriptionClass(): string {
      return 'centered mb-24 text-body sm:text-lead';
    },
    secondaryDescriptionClass(): string {
      return `${this.descriptionClass} whitespace-pre-line`;
    },
  },
};
</script>

<style scoped lang="scss">
.form-stepper-card {
  @media (max-width: 480px) {
    background-color: #fbfbfb !important;
  }
}
</style>
